import { getValueType, isDraftSensitiveValueType } from 'common/types/summaryDefinitions';
import { ChartDataPoint } from 'components/Charts/types';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { ChartDataResponse } from 'services/summaryDefinitions';
import formatNumbers from 'utilities/formatNumbers';

// Row status delivered by the chart-data endpoint (same values as the table rows)
const DRAFT_STATUS = 'DRAFT';

const isDraftSensitiveMetric = (metricKey: string): boolean => {
  const valueType = getValueType(metricKey);
  return valueType ? isDraftSensitiveValueType(valueType) : false;
};

/**
 * Build the popover series for one entity + metric. Draft-period points are
 * dropped only for valuation-derived (draft-sensitive) metrics, so the time
 * axis bridges them; other metrics keep charting through draft periods.
 */
export const buildSeriesPoints = (
  chartData: ChartDataResponse,
  entityName: string,
  metricKey: string
): ChartDataPoint[] => {
  const values = chartData.entities[entityName]?.[metricKey];
  if (!values) {
    return [];
  }

  const statuses = chartData.statuses?.[entityName] ?? [];
  const excludeDraftPeriods = isDraftSensitiveMetric(metricKey);

  return chartData.measurement_dates.reduce<ChartDataPoint[]>((points, date, index) => {
    const value = values[index];
    const isDraftPeriod = excludeDraftPeriods && statuses[index] === DRAFT_STATUS;
    if (value != null && !isDraftPeriod) {
      points.push({ x: date, y: value });
    }
    return points;
  }, []);
};

/** Format a chart value with the same format object the source cell uses. */
export const formatMetricValue = (value: number | null | undefined, format?: ICell['format']): string => {
  if (value == null) {
    return '-';
  }
  if (!format) {
    return value.toLocaleString('en-US');
  }
  // Negatives are allowed everywhere the summary tables render, so keep the
  // chart in step with the cell it was opened from.
  return String(formatNumbers({ value, format, allowNegativeValue: true } as Parameters<typeof formatNumbers>[0]));
};
